import { Chip } from '@material-ui/core';
import { FormStore } from '@roc/feature-app-core';
import { CurrencyField, MultiSelectField, TextField } from '@roc/ui';
import { isNotBlank } from 'libs/feature-utils/src/utils';
import { observer } from 'mobx-react';

const SELECT_ALL_VALUE = Symbol('SELECT_ALL');
const CustomMultiSelectField = observer(
  ({
    testId,
    label,
    options,
    value,
    onChange,
    disabled,
    error,
    helperText,
  }: {
    testId: string;
    label: string;
    options: { label: string; value: number }[];
    value: number[];
    onChange: (value: number[]) => void;
    disabled?: boolean;
    error?: string;
    helperText?: string;
  }) => {
    const showSelectAll = options.length > 1;
    const getOptionsFromValues = (options, values) =>
      values.map(value => ({
        label: options.find(o => o.value === value)?.label,
        value,
      }));

    const selectAllOption = {
      label: 'Select All',
      value: SELECT_ALL_VALUE,
    };

    const allOptionsSelected = value.length === options.length;

    const handleChange = (e, selectedOptions) => {
      const allOptionsValues = options.map(option => option.value);
      const selectedValues = selectedOptions.map(option => option.value);

      const selectAllClicked =
        showSelectAll &&
        e.target?.attributes?.['data-option-index']?.value === '0';

      const selectAllSelected = selectedValues.includes(SELECT_ALL_VALUE);

      if (selectAllClicked && selectAllSelected) {
        onChange(allOptionsValues);
      } else if (selectAllClicked && !selectAllSelected) {
        onChange([]);
      } else {
        onChange(selectedValues.filter(value => value !== SELECT_ALL_VALUE));
      }
    };

    return (
      <>
        <MultiSelectField
          label={label}
          testId={testId}
          value={[
            allOptionsSelected ? selectAllOption : null,
            ...getOptionsFromValues(options, value),
          ].filter(Boolean)}
          required={true}
          standaloneLabel={true}
          options={[showSelectAll ? selectAllOption : null, ...options].filter(
            Boolean
          )}
          onChange={handleChange}
          getOptionSelected={(option, selectedValue) =>
            option.value == selectedValue?.value
          }
          renderTags={(selectedOptions, getTagProps) =>
            selectedOptions.map((option, index) =>
              option.value !== SELECT_ALL_VALUE ? (
                <Chip label={option.label} {...getTagProps({ index })} />
              ) : (
                <></>
              )
            )
          }
          renderInput={params => (
            <TextField
              {...params}
              testId={testId}
              variant="outlined"
              disabled={disabled}
              error={isNotBlank(error)}
              helperText={error}
            />
          )}
        />
      </>
    );
  }
);

export const SelectPropertiesField = observer(
  ({
    properties,
    formStore,
    fieldName,
  }: {
    properties: any[];
    formStore: any;
    fieldName: string;
  }) => {
    const field = formStore.form.fields[fieldName];
    const propertyOptions =
      properties?.map(property => ({
        label: property.propertyAddress,
        value: property.propertyId,
      })) || [];

    return (
      <>
        <CustomMultiSelectField
          testId="select-properties"
          label="Select Properties"
          options={propertyOptions}
          value={field.value || []}
          onChange={value => {
            formStore.onFieldChange(fieldName, value);
            formStore.onFieldChange('clientValuationReferenceIds', []);
          }}
          error={field.error}
        />
      </>
    );
  }
);

export const SelectOrdersField = observer(
  ({
    valuations,
    formStore,
    fieldName,
  }: {
    valuations: any[];
    formStore: FormStore;
    fieldName: string;
  }) => {
    const field = formStore.form.fields[fieldName];
    const orderOptions =
      valuations?.map(order => ({
        label: order.vendorOrderNumber,
        value: order.clientValuationReferenceId,
      })) || [];

    return (
      <>
        <CustomMultiSelectField
          testId="select-orders"
          label="Select Orders"
          options={orderOptions}
          value={field.value || []}
          onChange={value => formStore.onFieldChange(fieldName, value)}
          error={field.error}
        />
      </>
    );
  }
);

export const ReasonField = observer(
  ({ formStore, fieldName }: { formStore: FormStore; fieldName: string; }) => {
    const field = formStore.form.fields[fieldName];
    return (
      <TextField
        testId="reason"
        variant="outlined"
        label="Reason"
        standaloneLabel
        fullWidth
        value={field.value || ''}
        onChange={e => formStore.onFieldChange(fieldName, e.target.value)}
        error={isNotBlank(field.error)}
        helperText={field.error}
      />
    );
  }
);

export const AmountField = observer(
  ({ formStore, fieldName }: { formStore: FormStore; fieldName: string }) => {
    const field = formStore.form.fields[fieldName];
    return (
      <CurrencyField
        testId="amount"
        variant="outlined"
        label="Amount"
        standaloneLabel
        fullWidth
        value={field.value || ''}
        onChange={(name, value) => formStore.onFieldChange(fieldName, Number(value))}
        error={isNotBlank(field.error)}
        errorText={field.error}
      />
    );
  }
);


export const CommentsField = observer(
  ({ formStore, fieldName }: { formStore: FormStore; fieldName: string }) => {
    const field = formStore.form.fields[fieldName];
    return (
      <TextField
        testId="reason"
        variant="outlined"
        label="Comments"
        standaloneLabel
        fullWidth
        multiline={true}
        minRows={5}
        value={field.value || ''}
        onChange={e => formStore.onFieldChange(fieldName, e.target.value)}
        error={isNotBlank(field.error)}
        helperText={field.error}
      />
    );
  }
);
